import { Popover } from '@base-ui/react/popover'
import { isSome } from '@mbfab/shared'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

// 使い方のルール: docs/develop/client_ui/components/data-table.md

// セル内の文字列が長いときの見せ方。
// - truncate: 1行で末尾を「…」にする（既定）
// - clamp: 2行まで折り返し、それ以上は「…」にする
// - wrap: 最大幅で折り返して全文を出す（省略しない）
// - none: 折り返さず全文を1行で出す（列幅の上限なし。従来の挙動）
export type OverflowMode = 'truncate' | 'clamp' | 'wrap' | 'none'

// 列幅の既定上限。機械式継手の品名など、1セルに数十文字入る値で表全体が横に伸びて
// 他の列が画面外へ押し出されたため（2026-10-31）、何も指定しなくても上限を持たせる
export const DEFAULT_OVERFLOW_MAX_WIDTH_CLASS = 'max-w-80'

const modeClass: Record<Exclude<OverflowMode, 'none'>, string> = {
  truncate: 'truncate',
  clamp: 'line-clamp-2 whitespace-normal break-words',
  wrap: 'whitespace-normal break-words',
}

function isOverflowing(element: HTMLElement, mode: OverflowMode) {
  if (mode === 'truncate') {
    return element.scrollWidth > element.clientWidth
  }
  if (mode === 'clamp') {
    return element.scrollHeight > element.clientHeight
  }
  return false
}

// 省略されたときだけ全文をポップオーバーで出すテキスト。
// 全セルに ResizeObserver を張ると行数×列数ぶん監視が増えるため、はみ出し判定は描画直後と
// ホバー・フォーカス時（フォント読み込み後の幅変化を拾う）にだけ行う
export function OverflowText({
  text,
  mode = 'truncate',
  maxWidthClass = DEFAULT_OVERFLOW_MAX_WIDTH_CLASS,
  className,
}: {
  text: string
  mode?: OverflowMode
  maxWidthClass?: string
  className?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const [overflowing, setOverflowing] = useState(false)

  const measure = useCallback(() => {
    const element = ref.current
    if (isSome(element)) {
      setOverflowing(isOverflowing(element, mode))
    }
  }, [mode])

  useLayoutEffect(() => {
    measure()
  }, [measure, text])

  if (mode === 'none') {
    return <>{text}</>
  }

  const textClass = cn('block min-w-0', maxWidthClass, modeClass[mode], className)

  if (!overflowing) {
    return (
      <span ref={ref} className={textClass} onPointerEnter={measure}>
        {text}
      </span>
    )
  }

  return (
    <Popover.Root>
      <Popover.Trigger
        nativeButton={false}
        openOnHover
        delay={300}
        render={
          <span
            ref={ref}
            // 省略されたセルはキーボードでも全文を読めるようにフォーカス可能にする
            tabIndex={0}
            className={cn(
              textClass,
              'cursor-default rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
            data-overflowing="true"
          />
        }
      >
        {text}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner className="z-50" side="bottom" align="start" sideOffset={4}>
          <Popover.Popup className="max-w-md rounded-lg border border-border bg-popover px-3 py-2 text-sm whitespace-pre-wrap break-words text-popover-foreground shadow-lg outline-none">
            {text}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}
